import {useUpdateUnitMutation} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {FullUnitFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type EditUnitModalUserProps = {
  unit: FullUnitFragment;
  onSuccess?: () => void;
};

const DECIMAL_CHOICES = [0, 1, 2] as const;

export const EditUnitModal = ({
  unit,
  onRequestClose,
  onSuccess,
}: EditUnitModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState(unit.name);
  const [singular, setSingular] = React.useState(unit.singular);
  const [plural, setPlural] = React.useState(unit.plural);
  const [displayDecimals, setDisplayDecimals] = React.useState(
    unit.displayDecimals,
  );
  const [description, setDescription] = React.useState(unit.description ?? '');
  const [error, setError] = React.useState<string | null>(null);

  const [updateUnit, {loading}] = useUpdateUnitMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await updateUnit({
        variables: {
          id: unit._id,
          name: name.trim(),
          singular: singular.trim(),
          plural: plural.trim(),
          displayDecimals,
          // Empty string is meaningful here: null means "no change" while
          // '' clears the stored description, so send the trimmed value
          // verbatim.
          description: description.trim(),
        },
      });
      onSuccess?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !name.trim() || !singular.trim() || !plural.trim()}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit Unit"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput value={name} onChange={setName} autoFocus />
          <p className="text-muted-foreground text-xs">
            Renaming never touches transactions — they store the unit's id.
          </p>
        </div>

        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">Singular</label>
            <TextInput value={singular} onChange={setSingular} />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">Plural</label>
            <TextInput value={plural} onChange={setPlural} />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Decimals</label>
          <div className="flex gap-2">
            {DECIMAL_CHOICES.map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => setDisplayDecimals(choice)}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  displayDecimals === choice
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {(3).toFixed(choice)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <Textarea value={description} onChange={setDescription} rows={2} />
        </div>
      </form>
    </Modal>
  );
};
